import { SEO } from '@/components/SEO';
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import LocationFAQ from "@/components/locations/LocationFAQ";
import LocationBlogLinks from "@/components/locations/LocationBlogLinks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Phone, Droplets, Sparkles, Home, Filter, Brush, Sun } from "lucide-react";
import { Link } from "react-router-dom";

const VenturaPage = () => {
  const navigate = useNavigate();

  const locationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Ventura Power Washing - Ventura",
    "description": "Professional pressure washing, window cleaning, and carpet cleaning services in Ventura, CA. Serving residential and commercial properties throughout Ventura.",
    "url": "https://www.venturapowerwashing.com/ventura",
    "telephone": "(805) 464-7166",
    "areaServed": {
      "@type": "City",
      "name": "Ventura",
      "containedInPlace": {
        "@type": "State",
        "name": "California"
      }
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 34.2746,
      "longitude": -119.2290
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Ventura",
      "addressRegion": "CA",
      "postalCode": "93001",
      "addressCountry": "US"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "07:00",
      "closes": "19:00"
    },
    "priceRange": "$$",
    "image": "https://www.venturapowerwashing.com/og-image.jpg",
    "sameAs": [
      "https://www.facebook.com/venturapowerwashing",
      "https://www.instagram.com/venturacleaningco",
      "https://www.yelp.com/biz/ventura-power-washing-thousand-oaks"
    ]
  };

  const services = [
    {
      icon: <Droplets className="h-8 w-8 text-ventura-blue" />,
      title: "Pressure Washing",
      description: "Deep cleaning for driveways, patios, decks, and exterior surfaces.",
      link: "/services/pressure-washing"
    },
    {
      icon: <Sparkles className="h-8 w-8 text-ventura-blue" />,
      title: "Window Cleaning",
      description: "Crystal-clear windows for homes and businesses.",
      link: "/services/window-washing"
    },
    {
      icon: <Home className="h-8 w-8 text-ventura-blue" />,
      title: "Carpet Cleaning",
      description: "Professional carpet and upholstery cleaning services.",
      link: "/services/carpet-cleaning"
    },
    {
      icon: <Filter className="h-8 w-8 text-ventura-blue" />,
      title: "Gutter Cleaning",
      description: "Keep gutters clear and protect your home from water damage.",
      link: "/services/gutter-cleaning"
    },
    {
      icon: <Brush className="h-8 w-8 text-ventura-blue" />,
      title: "Soft Wash",
      description: "Gentle cleaning for delicate surfaces like stucco and painted exteriors.",
      link: "/services/soft-wash"
    },
    {
      icon: <Sun className="h-8 w-8 text-ventura-blue" />,
      title: "Solar Panel Cleaning",
      description: "Maximize solar efficiency with professional panel maintenance.",
      link: "/services/solar-panel-cleaning"
    }
  ];

  const faqs = [
    {
      question: "How much does pressure washing cost in Ventura, CA?",
      answer: "Most driveway, patio, and house pressure washing jobs in Ventura run $150-$400, depending on square footage and surface type. We provide a free, no-obligation estimate for your property before any work begins."
    },
    {
      question: "How much does window cleaning cost in Ventura?",
      answer: "Residential window cleaning in Ventura typically costs $150-$300 for a standard home, inside and out. Coastal homes near Pierpont or the Ventura Keys often need more frequent cleaning due to salt spray."
    },
    {
      question: "Do you offer carpet cleaning in Ventura?",
      answer: "Yes. Whole-house carpet cleaning in Ventura generally runs $100-$300 depending on square footage and the number of rooms. We use deep extraction methods that remove sand, dirt, and moisture common in coastal homes."
    },
    {
      question: "Is solar panel cleaning worth it for Ventura homes?",
      answer: "Yes — Ventura's coastal salt air and marine layer moisture build up on panels quickly, and dirty panels can lose 15-30% efficiency. Professional solar panel cleaning in Ventura typically costs $150-$350 and pays for itself through improved energy production."
    }
  ];

  return (
    <>
      <SEO
        title="Pressure Washing & Cleaning Services in Ventura, CA | Ventura Power Washing"
        description="Professional pressure washing, window cleaning, and carpet cleaning services in Ventura, CA. Coastal cleaning experts serving 93001, 93003, and 93004 zip codes. Call (805) 464-7166!"
        keywords="pressure washing Ventura, window cleaning Ventura CA, carpet cleaning Ventura, power washing 93001, coastal cleaning Ventura"
        path="/ventura"
      >
        <script type="application/ld+json">
          {JSON.stringify(locationSchema)}
        </script>
      </SEO>

      <Navbar />
      
      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-24">
          <Breadcrumbs 
            items={[
              { label: "Home", href: "/" },
              { label: "Ventura", href: "/ventura" }
            ]} 
          />
        </div>

        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-ventura-blue/10 to-ventura-green/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Professional Cleaning Services in <span className="text-ventura-blue">Ventura, CA</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8">
                Your hometown cleaning experts! Trusted pressure washing, window cleaning, and carpet cleaning services for Ventura's coastal homes and businesses. Serving 93001, 93003, and 93004 zip codes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-ventura-blue hover:bg-ventura-blue/90 text-white"
                  onClick={() => window.location.href = 'tel:+18054647166'}
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call (805) 464-7166
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-ventura-blue text-ventura-blue hover:bg-ventura-blue/10"
                  onClick={() => navigate('/free-estimate')}
                >
                  Get Free Estimate
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
              Our Services in Ventura
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="mb-4">{service.icon}</div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link 
                      to={service.link}
                      className="text-ventura-blue hover:text-ventura-blue/80 font-medium inline-flex items-center"
                    >
                      Learn More →
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Local Area Information */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-foreground mb-6">
                What Makes Cleaning Ventura's Coastal Properties Different?
              </h2>
              <p className="text-muted-foreground mb-6">
                Ventura's coastal properties face salt air, marine layer moisture, and sand that build up faster than in inland
                Ventura County cities, requiring techniques calibrated for these conditions. As Ventura locals, we serve every
                neighborhood — Downtown, Midtown, Pierpont, the Avenue, East Ventura, and the hillside communities — with that
                coastal expertise built in.
              </p>
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">Areas We Serve</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Downtown Ventura</li>
                    <li>• Midtown</li>
                    <li>• Pierpont</li>
                    <li>• The Avenue</li>
                    <li>• East Ventura</li>
                    <li>• Ventura Keys</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">Coastal Cleaning Expertise</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Salt and mineral deposit removal</li>
                    <li>• Marine environment specialists</li>
                    <li>• Historic downtown building care</li>
                    <li>• Beachfront property cleaning</li>
                    <li>• 100% satisfaction guarantee</li>
                    <li>• Licensed and insured</li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 text-center">
                <Button 
                  size="lg"
                  className="bg-ventura-blue hover:bg-ventura-blue/90 text-white"
                  onClick={() => navigate('/free-estimate')}
                >
                  Get Your Free Ventura Estimate
                </Button>
              </div>
            </div>
          </div>
        </section>

        <LocationFAQ city="Ventura" faqs={faqs} />
        <LocationBlogLinks city="Ventura" />
      </main>

      <Footer />
    </>
  );
};

export default VenturaPage;
